import { sendMessageToWorkerWithResponse } from 'helpers/communication'
import { Order } from 'storeTypes'

/** SW/IDB reads should be fast; hanging leaves enrollment Continue disabled. */
export const RESOLVE_FULL_GOAL_ITEMS_TIMEOUT_MS = 3000

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
    let timeoutId: ReturnType<typeof setTimeout>
    return Promise.race([
        promise.finally(() => clearTimeout(timeoutId)),
        new Promise<T>((_resolve, reject) => {
            timeoutId = setTimeout(
                () =>
                    reject(
                        new Error(
                            `[resolveFullGoalItems] timed out after ${ms}ms`
                        )
                    ),
                ms
            )
        })
    ])
}

/**
 * Full client orders for discount tier math (delivered + undelivered).
 * Prefer Redux when it matches clientCode; otherwise SW/IDB (survives
 * resetClientOrdersHandler after leaving client details).
 *
 * Throws on SW failure / timeout so callers can distinguish "no orders"
 * from "fetch failed" and avoid silently overstating discounts. Always
 * settles (incl. timeout) so EnrollmentBase can clear goalItemsFetchPending.
 */
export const resolveFullGoalItems = async (
    clientCode: string | undefined | null,
    reduxOrders: Order[] = []
): Promise<Order[]> => {
    if (!clientCode) {
        return []
    }

    const ordersMatchClient =
        reduxOrders.length > 0 &&
        reduxOrders.every((order) => order.client_code === clientCode)

    if (ordersMatchClient) {
        return reduxOrders
    }

    try {
        const { result } = await withTimeout(
            sendMessageToWorkerWithResponse({
                'get client orders': clientCode
            }),
            RESOLVE_FULL_GOAL_ITEMS_TIMEOUT_MS
        )
        return Array.isArray(result) ? (result as Order[]) : []
    } catch (err) {
        console.error('[resolveFullGoalItems] SW fetch failed:', err)
        throw err
    }
}
